/**
 * ut-collection.js
 * Vanilla JS — zero dependencies.
 * Handles:
 *   1. Announcement bar message rotation
 *   2. Filter chips — active state + URL update
 *   3. Sort select — URL update on change
 *   4. Grid view toggle (3-col / 4-col)
 *   5. Floating filter button — open/close drawer
 *   6. Filter drawer — apply/clear filters
 *   7. Filter badge — count active filters
 *   8. Load More — AJAX append next page
 *   9. Product image hover swap (redundant safety, CSS handles it)
 *  10. Drag-scroll for explore collections row
 */

(function () {
  'use strict';

  // ======================================================================
  // 1. ANNOUNCEMENT BAR ROTATION
  // ======================================================================
  function initAnnouncementBar() {
    var bar = document.getElementById('ut-announcement-bar');
    if (!bar) return;
    var messages = bar.querySelectorAll('.ut-announcement-bar__message');
    if (messages.length <= 1) return;
    var current = 0;
    var interval = parseInt(bar.dataset.interval || '4000', 10);
    setInterval(function () {
      messages[current].classList.remove('is-active');
      current = (current + 1) % messages.length;
      messages[current].classList.add('is-active');
    }, interval);
  }

  // ======================================================================
  // 2. FILTER CHIPS — Active state + URL update
  // ======================================================================
  function initFilterChips() {
    var chips = document.querySelectorAll('.ut-filter-chips__chip');
    if (!chips.length) return;

    chips.forEach(function (chip) {
      chip.addEventListener('click', function (e) {
        e.preventDefault();
        var filterType = chip.dataset.filterType;
        var url = new URL(window.location.href);

        // Remove existing product_type filter
        url.searchParams.delete('filter.p.product_type');

        if (filterType !== '') {
          url.searchParams.set('filter.p.product_type', filterType);
        }

        // Remove page param when filtering
        url.searchParams.delete('page');

        window.location.href = url.toString();
      });
    });
  }

  // ======================================================================
  // 3. SORT SELECT — URL param update
  // ======================================================================
  function initSortSelect() {
    var sortSelect = document.getElementById('ut-sort-select');
    if (!sortSelect) return;

    sortSelect.addEventListener('change', function () {
      var url = new URL(window.location.href);
      if (sortSelect.value && sortSelect.value !== 'manual') {
        url.searchParams.set('sort_by', sortSelect.value);
      } else {
        url.searchParams.delete('sort_by');
      }
      url.searchParams.delete('page');
      window.location.href = url.toString();
    });
  }

  // ======================================================================
  // 4. GRID VIEW TOGGLE (3-col / 4-col)
  // ======================================================================
  function initGridToggle() {
    var btns = document.querySelectorAll('.ut-toolbar__grid-btn');
    var grid = document.getElementById('ut-product-grid');
    if (!btns.length || !grid) return;

    btns.forEach(function (btn) {
      btn.addEventListener('click', function () {
        var cols = btn.dataset.gridCols;

        // Update button states
        btns.forEach(function (b) {
          b.classList.remove('is-active');
          b.setAttribute('aria-pressed', 'false');
        });
        btn.classList.add('is-active');
        btn.setAttribute('aria-pressed', 'true');

        // Update grid
        grid.dataset.cols = cols;
        grid.style.gridTemplateColumns = 'repeat(' + cols + ', 1fr)';
      });
    });
  }

  // ======================================================================
  // 5. FLOATING FILTER BUTTON — Open/Close Drawer
  // ======================================================================
  function initFloatingFilter() {
    var openBtn = document.getElementById('ut-filter-open-btn');
    var closeBtn = document.getElementById('ut-filter-close-btn');
    var drawer = document.getElementById('ut-filter-drawer');
    var backdrop = document.getElementById('ut-filter-backdrop');

    if (!openBtn || !drawer) return;

    function openDrawer() {
      drawer.classList.add('is-open');
      if (backdrop) backdrop.classList.add('is-open');
      document.body.classList.add('ut-filter-open');
      openBtn.setAttribute('aria-expanded', 'true');
      // Focus first focusable element in drawer
      var firstFocusable = drawer.querySelector('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
      if (firstFocusable) firstFocusable.focus();
    }

    function closeDrawer() {
      drawer.classList.remove('is-open');
      if (backdrop) backdrop.classList.remove('is-open');
      document.body.classList.remove('ut-filter-open');
      openBtn.setAttribute('aria-expanded', 'false');
      openBtn.focus();
    }

    openBtn.addEventListener('click', openDrawer);
    if (closeBtn) closeBtn.addEventListener('click', closeDrawer);
    if (backdrop) backdrop.addEventListener('click', closeDrawer);

    // Escape key closes drawer
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && drawer.classList.contains('is-open')) {
        closeDrawer();
      }
    });

    // Trap focus inside open drawer
    drawer.addEventListener('keydown', function (e) {
      if (e.key !== 'Tab') return;
      var focusable = drawer.querySelectorAll('button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])');
      var first = focusable[0];
      var last = focusable[focusable.length - 1];
      if (e.shiftKey) {
        if (document.activeElement === first) {
          e.preventDefault();
          last.focus();
        }
      } else {
        if (document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    });
  }

  // ======================================================================
  // 6. FILTER DRAWER — Apply / Clear / Badge
  // ======================================================================
  function initFilterDrawer() {
    var applyBtn = document.getElementById('ut-filter-apply');
    var clearBtn = document.getElementById('ut-filter-clear');
    var badge = document.getElementById('ut-filter-badge');
    var drawer = document.getElementById('ut-filter-drawer');

    if (!applyBtn || !drawer) return;

    // Size chips toggle
    var sizeChips = drawer.querySelectorAll('.ut-filter-size-chip');
    sizeChips.forEach(function (chip) {
      chip.addEventListener('click', function () {
        var isActive = chip.classList.contains('is-active');
        chip.classList.toggle('is-active', !isActive);
        chip.setAttribute('aria-pressed', (!isActive).toString());
        updateBadge();
      });
    });

    // Update active filter badge count
    function countActiveFilters() {
      var count = 0;
      // Checkboxes
      var checkboxes = drawer.querySelectorAll('input[type="checkbox"]:checked');
      count += checkboxes.length;
      // Size chips
      var activeChips = drawer.querySelectorAll('.ut-filter-size-chip.is-active');
      count += activeChips.length;
      // Price radio (if not "All Prices")
      var priceRadio = drawer.querySelector('input[name="price_range"]:checked');
      if (priceRadio && priceRadio.value !== '') count++;
      // Sort radio (if not default)
      var sortRadio = drawer.querySelector('input[name="drawer_sort"]:checked');
      if (sortRadio && sortRadio.value !== 'manual') count++;
      return count;
    }

    function updateBadge() {
      if (!badge) return;
      var count = countActiveFilters();
      badge.textContent = count;
      badge.classList.toggle('is-visible', count > 0);
    }

    // Listen on checkboxes and radios for badge update
    drawer.querySelectorAll('input[type="checkbox"], input[type="radio"]').forEach(function (input) {
      input.addEventListener('change', updateBadge);
    });

    // Apply Filters
    applyBtn.addEventListener('click', function () {
      var url = new URL(window.location.href);

      // Clear existing filter params
      Array.from(url.searchParams.keys()).forEach(function (key) {
        if (key.startsWith('filter.') || key === 'sort_by' || key === 'page') {
          url.searchParams.delete(key);
        }
      });

      // Product type checkboxes
      drawer.querySelectorAll('input[name="filter.p.product_type"]:checked').forEach(function (cb) {
        url.searchParams.append('filter.p.product_type', cb.value);
      });

      // Size chips
      drawer.querySelectorAll('.ut-filter-size-chip.is-active').forEach(function (chip) {
        url.searchParams.append('filter.v.option.size', chip.dataset.filterValue);
      });

      // Price range
      var priceRadio = drawer.querySelector('input[name="price_range"]:checked');
      if (priceRadio && priceRadio.value !== '') {
        url.searchParams.set('filter.v.price.gte', priceRadio.dataset.min);
        url.searchParams.set('filter.v.price.lte', priceRadio.dataset.max);
      }

      // Sort
      var sortRadio = drawer.querySelector('input[name="drawer_sort"]:checked');
      if (sortRadio && sortRadio.value && sortRadio.value !== 'manual') {
        url.searchParams.set('sort_by', sortRadio.value);
      }

      window.location.href = url.toString();
    });

    // Clear All
    clearBtn.addEventListener('click', function () {
      // Uncheck all checkboxes
      drawer.querySelectorAll('input[type="checkbox"]').forEach(function (cb) {
        cb.checked = false;
      });
      // Deactivate size chips
      drawer.querySelectorAll('.ut-filter-size-chip.is-active').forEach(function (chip) {
        chip.classList.remove('is-active');
        chip.setAttribute('aria-pressed', 'false');
      });
      // Reset price radio
      var firstPrice = drawer.querySelector('input[name="price_range"]');
      if (firstPrice) firstPrice.checked = true;
      // Reset sort radio
      var firstSort = drawer.querySelector('input[name="drawer_sort"]');
      if (firstSort) firstSort.checked = true;
      updateBadge();
    });

    // Init badge from current URL state
    (function syncBadgeFromURL() {
      var url = new URL(window.location.href);
      var count = 0;
      url.searchParams.forEach(function (val, key) {
        if (key.startsWith('filter.') || key === 'sort_by') count++;
      });
      if (badge && count > 0) {
        badge.textContent = count;
        badge.classList.add('is-visible');
      }
    })();
  }

  // ======================================================================
  // 7. LOAD MORE — AJAX append next page of products
  // ======================================================================
  function initLoadMore() {
    var btn = document.getElementById('ut-load-more-btn');
    var grid = document.getElementById('ut-product-grid');
    if (!btn || !grid) return;

    btn.addEventListener('click', function () {
      var nextUrl = btn.dataset.nextUrl;
      if (!nextUrl) return;

      btn.disabled = true;
      btn.classList.add('is-loading');
      btn.textContent = 'Loading';

      fetch(nextUrl)
        .then(function (response) {
          if (!response.ok) throw new Error('Network error');
          return response.text();
        })
        .then(function (html) {
          var parser = new DOMParser();
          var doc = parser.parseFromString(html, 'text/html');
          var newGrid = doc.getElementById('ut-product-grid');
          var newCards = newGrid ? newGrid.querySelectorAll('.ut-product-card') : [];

          // Append new cards with fade-in animation class
          newCards.forEach(function (card) {
            card.classList.add('is-new');
            grid.appendChild(card);
          });

          // Update Load More button — check for next page
          var newLoadMore = doc.getElementById('ut-load-more-btn');
          if (newLoadMore && newLoadMore.dataset.nextUrl) {
            btn.dataset.nextUrl = newLoadMore.dataset.nextUrl;
            btn.disabled = false;
            btn.classList.remove('is-loading');
            btn.textContent = 'Load More';
          } else {
            // No more pages
            var wrap = document.getElementById('ut-load-more-wrap');
            if (wrap) wrap.style.display = 'none';
          }

          // Remove animation class after animation completes
          setTimeout(function () {
            newCards.forEach(function (card) {
              card.classList.remove('is-new');
            });
          }, 800);
        })
        .catch(function (err) {
          console.error('Load more failed:', err);
          btn.disabled = false;
          btn.classList.remove('is-loading');
          btn.textContent = 'Load More';
        });
    });
  }

  // ======================================================================
  // 8. DRAG SCROLL — Explore Collections Row
  // ======================================================================
  function initDragScroll() {
    var scrollWrap = document.getElementById('ut-explore-scroll');
    if (!scrollWrap) return;

    var isDown = false;
    var startX;
    var scrollLeft;

    scrollWrap.addEventListener('mousedown', function (e) {
      isDown = true;
      scrollWrap.classList.add('is-dragging');
      startX = e.pageX - scrollWrap.offsetLeft;
      scrollLeft = scrollWrap.scrollLeft;
      e.preventDefault();
    });

    scrollWrap.addEventListener('mouseleave', function () {
      isDown = false;
      scrollWrap.classList.remove('is-dragging');
    });

    scrollWrap.addEventListener('mouseup', function () {
      isDown = false;
      scrollWrap.classList.remove('is-dragging');
    });

    scrollWrap.addEventListener('mousemove', function (e) {
      if (!isDown) return;
      e.preventDefault();
      var x = e.pageX - scrollWrap.offsetLeft;
      var walk = (x - startX) * 2;
      scrollWrap.scrollLeft = scrollLeft - walk;
    });
  }

  // ======================================================================
  // INIT — Wait for DOM ready
  // ======================================================================
  function init() {
    initAnnouncementBar();
    initFilterChips();
    initSortSelect();
    initGridToggle();
    initFloatingFilter();
    initFilterDrawer();
    initLoadMore();
    initDragScroll();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
